import {
  EventType,
  type MatrixClient,
  type MatrixEvent,
  RelationType,
  type Room,
} from 'matrix-js-sdk'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import type { MessageReader } from '@/modules/chat-matrix-fast/services/chatApiService'
import { resolveMatrixSenderProfile } from '@/modules/chat-matrix-fast/services/matrixProfileCache'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { isMatrixHiddenUser } from '@/modules/chat-matrix-fast/constants/chat'
import { withMatrixAuthRetry } from '@/modules/chat-matrix-fast/services/matrixAuthRecovery'

export type MatrixReadReceiptResult =
  | { status: 'sent'; eventId: string }
  | { status: 'skipped'; reason: string; eventId?: string }
  | { status: 'no-room' }
  | { status: 'no-event' }
  | { status: 'error'; error: string }

const lastSentReadReceiptByRoom = new Map<string, string>()
const inFlightReadReceiptByRoom = new Map<string, Promise<MatrixReadReceiptResult>>()
const lastReadReceiptErrorToastAt = new Map<string, number>()
const READ_RECEIPT_ERROR_TOAST_COOLDOWN_MS = 15_000

/** Показать ошибку Matrix read receipt (с антиспамом при ретраях). */
export function notifyMatrixReadReceiptError(error: unknown, roomId?: string): void {
  const key = roomId?.trim() || '*'
  const now = Date.now()
  const lastAt = lastReadReceiptErrorToastAt.get(key) ?? 0
  if (now - lastAt < READ_RECEIPT_ERROR_TOAST_COOLDOWN_MS) {
    return
  }
  lastReadReceiptErrorToastAt.set(key, now)

  const detail = error instanceof Error ? error.message : String(error ?? '').trim()
  useFeedbackStore().showToast({
    type: 'error',
    message: detail
      ? `Не удалось отметить сообщения прочитанными: ${detail}`
      : 'Не удалось отметить сообщения прочитанными',
    time: 7000,
  })
}

/** skipped — локальный кэш, не ACK homeserver, пока raw unread > 0. */
export function isMatrixReadReceiptConfirmed(
  result: MatrixReadReceiptResult,
  rawUnreadCount: number,
): boolean {
  if (result.status === 'sent') {
    return true
  }

  if (result.status === 'skipped') {
    return rawUnreadCount <= 0
  }

  return false
}

function isReplaceEvent(event: MatrixEvent): boolean {
  return event.getRelation()?.rel_type === RelationType.Replace
}

function collectRoomEventsForReadReceipt(room: Room): MatrixEvent[] {
  const byId = new Map<string, MatrixEvent>()

  const add = (event: MatrixEvent | null | undefined) => {
    const eventId = event?.getId()
    if (!event || !eventId || byId.has(eventId)) {
      return
    }
    byId.set(eventId, event)
  }

  for (const event of room.getLiveTimeline().getEvents()) {
    add(event)
  }

  for (const thread of room.getThreads()) {
    for (const event of thread.events) {
      add(event)
    }
    add(thread.replyToEvent)
  }

  return [...byId.values()]
}

function pickLatestMatchingEvent(
  events: MatrixEvent[],
  predicate: (event: MatrixEvent) => boolean,
): MatrixEvent | undefined {
  let latest: MatrixEvent | undefined
  let latestTs = -1

  for (const event of events) {
    if (!predicate(event)) {
      continue
    }

    const ts = event.getTs()
    if (!latest || ts >= latestTs) {
      latest = event
      latestTs = ts
    }
  }

  return latest
}

function isRoomMessageEventForReadMarker(event: MatrixEvent): boolean {
  if (event.getType() !== EventType.RoomMessage || isReplaceEvent(event)) {
    return false
  }

  const eventId = event.getId()
  return Boolean(eventId && !eventId.startsWith('~'))
}

/** Последнее читаемое сообщение комнаты, включая треды — live timeline хвост не видит. */
function getLastMessageEventForReadMarker(room: Room): MatrixEvent | undefined {
  return pickLatestMatchingEvent(collectRoomEventsForReadReceipt(room), isRoomMessageEventForReadMarker)
}

/** Маркер дошёл до последнего читаемого события (после sync / local echo). */
export function isMatrixReadMarkerAtLastReadable(room: Room, userId: string): boolean {
  const lastEvent = getLastMessageEventForReadMarker(room)
  const lastEventId = lastEvent?.getId()
  if (!lastEventId) {
    return true
  }

  const readUpToId = room.getEventReadUpTo(userId)
  if (!readUpToId) {
    return false
  }

  if (readUpToId === lastEventId) {
    return true
  }

  const comparison = room.compareEventOrdering(readUpToId, lastEventId)
  if (comparison !== null) {
    return comparison >= 0
  }

  const readEvent = room.findEventById(readUpToId)
  if (!readEvent) {
    return false
  }

  return readEvent.getTs() >= lastEvent.getTs()
}

const READ_ACK_WAIT_MS = [0, 200, 500, 1000]

/** Ждём sync: raw unread 0 или getEventReadUpTo на последнем читаемом. */
export async function waitForMatrixReadCaughtUp(
  room: Room,
  userId: string,
  isRawUnreadZero: () => boolean,
): Promise<boolean> {
  for (const delay of READ_ACK_WAIT_MS) {
    if (delay) {
      await new Promise(resolve => setTimeout(resolve, delay))
    }

    if (isRawUnreadZero() || isMatrixReadMarkerAtLastReadable(room, userId)) {
      return true
    }
  }

  return isRawUnreadZero() || isMatrixReadMarkerAtLastReadable(room, userId)
}

function shouldSkipReadReceipt(room: Room, roomId: string, eventId: string): boolean {
  const cachedId = lastSentReadReceiptByRoom.get(roomId)
  if (!cachedId) {
    return false
  }

  if (cachedId === eventId) {
    return true
  }

  const comparison = room.compareEventOrdering(cachedId, eventId)
  return comparison !== null && comparison >= 0
}

function isOwnMessage(message: IMessage, myUserId: string): boolean {
  const authorId = String(message.author_id || message.created_by?.id || message.author)
  return authorId === myUserId
}

function isMessageDelivered(room: Room, message: IMessage): boolean {
  const eventId = message.matrix_event_id
  if (!eventId || eventId.startsWith('~')) {
    return false
  }

  if (room.hasPendingEvent(eventId)) {
    return false
  }

  return Boolean(room.findEventById(eventId))
}

/** Только реальный read receipt — без hasUserReadEvent (там «sender of latest = read all»). */
function isEventReadByUser(room: Room, userId: string, eventId: string): boolean {
  if (!room.findEventById(eventId)) {
    return false
  }

  const readUpToEventId = room.getEventReadUpTo(userId)
  if (!readUpToEventId) {
    return false
  }

  const comparison = room.compareEventOrdering(readUpToEventId, eventId)
  return comparison !== null && comparison >= 0
}

function collectUsersWhoReadEvent(
  room: Room,
  matrixEventId: string,
  myUserId: string,
): string[] {
  const event = room.findEventById(matrixEventId)
  if (!event) {
    return []
  }

  const userIds = new Set<string>()

  for (const userId of room.getUsersReadUpTo(event)) {
    if (userId && userId !== myUserId && !isMatrixHiddenUser(userId)) {
      userIds.add(userId)
    }
  }

  for (const receipt of room.getReceiptsForEvent(event)) {
    if (receipt.userId && receipt.userId !== myUserId && !isMatrixHiddenUser(receipt.userId)) {
      userIds.add(receipt.userId)
    }
  }

  for (const member of room.getJoinedMembers()) {
    const userId = member.userId
    if (!userId || userId === myUserId || isMatrixHiddenUser(userId)) {
      continue
    }

    if (isEventReadByUser(room, userId, matrixEventId)) {
      userIds.add(userId)
    }
  }

  return Array.from(userIds)
}

function isMessageReadByOthers(
  room: Room,
  message: IMessage,
  myUserId: string,
): boolean {
  const eventId = message.matrix_event_id
  if (!eventId || !room.findEventById(eventId)) {
    return false
  }

  return collectUsersWhoReadEvent(room, eventId, myUserId).length > 0
}

export async function getMatrixMessageReaders(
  matrixClient: MatrixClient,
  room: Room,
  matrixEventId: string,
): Promise<MessageReader[]> {
  const myUserId = matrixClient.getUserId() ?? ''

  await room.loadMembersIfNeeded()

  const userIds = collectUsersWhoReadEvent(room, matrixEventId, myUserId)

  return Promise.all(
    userIds.map(async userId => {
      const profile = await resolveMatrixSenderProfile(matrixClient, userId, room)
      const avatar = profile.avatarUrl ?? undefined

      return {
        id: userId,
        user: {
          id: userId,
          first_name: profile.displayName,
          last_name: '',
          avatar,
        },
        avatar,
        read_at: '',
      }
    }),
  )
}

export function enrichMessagesWithReadReceipts(
  matrixClient: MatrixClient,
  room: Room,
  messages: IMessage[],
): IMessage[] {
  const myUserId = matrixClient.getUserId() ?? ''
  if (!myUserId) {
    return messages
  }

  return messages.map(message => {
    if (!isOwnMessage(message, myUserId)) {
      return {
        ...message,
        is_delivered: true,
      }
    }

    const isDelivered = isMessageDelivered(room, message)
    const isRead = isDelivered && isMessageReadByOthers(room, message, myUserId)

    return {
      ...message,
      is_delivered: isDelivered,
      is_read: isRead,
    }
  })
}

export async function sendMatrixReadReceiptForEvent(
  matrixClient: MatrixClient,
  roomId: string,
  eventId: string,
): Promise<void> {
  const room = matrixClient.getRoom(roomId)
  if (!room) {
    return
  }

  const event = room.findEventById(eventId)
  if (!event || !isRoomMessageEventForReadMarker(event)) {
    return
  }

  if (shouldSkipReadReceipt(room, roomId, eventId)) {
    return
  }

  try {
    await withMatrixAuthRetry(async () => {
      await matrixClient.setRoomReadMarkers(roomId, eventId, event)
    })
    lastSentReadReceiptByRoom.set(roomId, eventId)
  } catch (error) {
    notifyMatrixReadReceiptError(error, roomId)
  }
}

export async function sendMatrixReadReceiptForRoom(
  matrixClient: MatrixClient,
  roomId: string,
): Promise<MatrixReadReceiptResult> {
  const inFlight = inFlightReadReceiptByRoom.get(roomId)
  if (inFlight) {
    return inFlight
  }

  const sendPromise = (async (): Promise<MatrixReadReceiptResult> => {
    const room = matrixClient.getRoom(roomId)
    if (!room) {
      return { status: 'no-room' }
    }

    const lastEvent = getLastMessageEventForReadMarker(room)
    const eventId = lastEvent?.getId()
    if (!lastEvent || !eventId) {
      return { status: 'no-event' }
    }

    if (shouldSkipReadReceipt(room, roomId, eventId)) {
      return {
        status: 'skipped',
        reason: 'already-sent-or-newer-marker',
        eventId,
      }
    }

    // Блокируем параллельные POST до завершения — иначе шторм receipt/read_markers.
    lastSentReadReceiptByRoom.set(roomId, eventId)

    try {
      await withMatrixAuthRetry(async () => {
        await matrixClient.setRoomReadMarkers(roomId, eventId, lastEvent)
      })
      return { status: 'sent', eventId }
    } catch (error) {
      if (lastSentReadReceiptByRoom.get(roomId) === eventId) {
        lastSentReadReceiptByRoom.delete(roomId)
      }
      return { status: 'error', error: String(error) }
    }
  })()

  inFlightReadReceiptByRoom.set(roomId, sendPromise)
  try {
    return await sendPromise
  } finally {
    if (inFlightReadReceiptByRoom.get(roomId) === sendPromise) {
      inFlightReadReceiptByRoom.delete(roomId)
    }
  }
}

export function resetMatrixReadReceiptCache(roomId?: string): void {
  if (roomId) {
    lastSentReadReceiptByRoom.delete(roomId)
    return
  }

  lastSentReadReceiptByRoom.clear()
}

function isEventAfterReadMarker(
  room: Room,
  readUpToId: string | undefined,
  event: MatrixEvent,
): boolean {
  const eventId = event.getId()
  if (!eventId) {
    return false
  }

  if (!readUpToId) {
    return true
  }

  const comparison = room.compareEventOrdering(readUpToId, eventId)
  if (comparison !== null) {
    return comparison < 0
  }

  const readEvent = room.findEventById(readUpToId)
  if (!readEvent) {
    return true
  }

  return event.getTs() > readEvent.getTs()
}

/** Сбрасывает фантомный unread (например, удалённое сообщение после read marker) */
export async function reconcileRoomUnreadCount(
  matrixClient: MatrixClient,
  room: Room,
): Promise<number> {
  const count = room.getUnreadNotificationCount?.() ?? 0
  if (count <= 0) {
    return 0
  }

  const myUserId = matrixClient.getUserId()
  if (!myUserId) {
    return count
  }

  const readUpToId = room.getEventReadUpTo(myUserId) ?? undefined
  let hasUnreadFromOthers = false

  for (const event of collectRoomEventsForReadReceipt(room)) {
    const eventId = event.getId()
    if (!eventId || !isRoomMessageEventForReadMarker(event)) {
      continue
    }

    if (!isEventAfterReadMarker(room, readUpToId, event)) {
      continue
    }

    if (!event.isRedacted() && event.getSender() !== myUserId) {
      hasUnreadFromOthers = true
      break
    }
  }

  if (hasUnreadFromOthers) {
    return count
  }

  const lastEvent = getLastMessageEventForReadMarker(room)
  const lastEventId = lastEvent?.getId()
  if (lastEvent && lastEventId) {
    await sendMatrixReadReceiptForEvent(matrixClient, room.roomId, lastEventId)
  }

  return 0
}
